/**
 * Errores de pantalla (#116, 15-oct-2026, Marcos Challier: «por momentos queda pantalla en blanco»).
 *
 * Un error de JavaScript en el render dejaba la app EN BLANCO y nadie se enteraba: el servidor no falló y lo único que
 * lo sabía era la consola de ese navegador. Acá está cómo la app lo cuenta: `describir` arma lo que se manda
 * (PURO, con tests), `reportar` lo manda a `POST /api/front/errores` sin bloquear nada ni fallar nunca, con un tope por
 * pestaña para que un error en bucle no inunde; `instalarCaptura` suma lo que no pasa por React (`window.onerror` y las
 * promesas rechazadas). El error va SOLO a nuestro servidor: ningún servicio de terceros (16-oct-2026, José: se
 * retiró PostHog, que venía de fábrica en la base generada y mandaba las sesiones de la oficina a una cuenta ajena).
 *
 * Nada de esto decide nada: es para ver el error la próxima vez sin pedirle la consola a nadie.
 */
import { api, MARCA_CLIENTE } from "@/lib/api";

export const TOPE_POR_MINUTO = 5;
export const ORIGENES = ["render", "error", "promesa"];

/** El mensaje de cualquier cosa que se tire (Error, string, objeto, nada). PURO. */
export function mensajeDe(err) {
  if (err == null) return "Error sin mensaje";
  if (typeof err === "string") return err.slice(0, 500);
  if (err.message) return String(err.message).slice(0, 500);
  try {
    return JSON.stringify(err).slice(0, 500);
  } catch {
    return String(err).slice(0, 500);
  }
}

/** Lo que se manda al servidor. `info` es el objeto de React (`componentStack`); `ctx` trae ruta, pantalla, origen. PURO. */
export function describir(err, info = null, ctx = {}) {
  const origen = ORIGENES.includes(ctx.origen) ? ctx.origen : "render";
  return {
    mensaje: mensajeDe(err),
    stack: err && err.stack ? String(err.stack).slice(0, 8000) : null,
    componente: info && info.componentStack ? String(info.componentStack).slice(0, 3000) : null,
    ruta: (ctx.ruta ?? (typeof window !== "undefined" ? window.location.pathname + window.location.search : "")).slice(0, 300),
    pantalla: ctx.pantalla ? String(ctx.pantalla).slice(0, 80) : null,
    build: String(ctx.build ?? MARCA_CLIENTE).slice(0, 40),
    origen,
    ocurrido_ms: ctx.ahora ?? Date.now(),
  };
}

/** Tope por pestaña: hasta `tope` envíos por minuto, y el mismo error (mensaje + ruta) una vez por minuto. PURO. */
export function crearCupo(tope = TOPE_POR_MINUTO) {
  let minuto = null;
  let n = 0;
  const vistos = new Set();
  return {
    admite(descripcion, ahora = Date.now()) {
      const m = Math.floor(ahora / 60000);
      if (m !== minuto) { minuto = m; n = 0; vistos.clear(); }
      const clave = `${descripcion.mensaje}|${descripcion.ruta}`;
      if (n >= tope || vistos.has(clave)) return false;
      vistos.add(clave);
      n += 1;
      return true;
    },
  };
}

const CUPO = crearCupo();

/** Manda el error al servidor (best-effort: nunca tira, nunca bloquea). Devuelve si se mandó. */
export function reportar(err, info = null, ctx = {}, { cupo = CUPO, enviar = (d) => api.post("/front/errores", d, { __silent: true }) } = {}) {
  let d;
  try {
    d = describir(err, info, ctx);
  } catch {
    return false;
  }
  if (!cupo.admite(d)) return false;
  try {
    Promise.resolve(enviar(d)).catch(() => {});
  } catch { /* sin sesión, sin red: no importa */ }
  return true;
}

/** Lo que NO pasa por React: un `window.onerror` y las promesas rechazadas sin `catch`. Se instala una vez. */
export function instalarCaptura(win = typeof window !== "undefined" ? window : null, reportarFn = reportar) {
  if (!win || win.__qbCapturaErrores) return () => {};
  win.__qbCapturaErrores = true;
  const onError = (e) => { reportarFn(e?.error || e?.message || e, null, { origen: "error" }); };
  const onRechazo = (e) => { reportarFn(e?.reason, null, { origen: "promesa" }); };
  win.addEventListener("error", onError);
  win.addEventListener("unhandledrejection", onRechazo);
  return () => {
    win.removeEventListener("error", onError);
    win.removeEventListener("unhandledrejection", onRechazo);
    win.__qbCapturaErrores = false;
  };
}
